/**
 * Metas por competência — aberto como folha a partir de Vendas.
 *
 * Quem edita o quê segue o combinado com a diretoria: diretoria define a meta
 * global e a de cada regional; gerente regional reparte entre seus consultores;
 * financeiro apenas lê. O botão de salvar simplesmente não existe para quem não
 * pode — e o servidor recusa de novo, porque esconder botão não é autorização.
 */

import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Target, Check, Lock } from 'lucide-react';
import { api } from '../../lib/api.ts';
import { competencia, competenciaAtual, numero, percentual } from '../../lib/format.ts';
import { Botao, Campo, EstadoVazio, Esqueleto, Card } from '../ui/index.tsx';
import { useAuth } from '../../contexts/AuthContext.tsx';

interface LinhaMeta {
  escopo: 'global' | 'regional' | 'consultor';
  escopoId: string | null;
  nome: string;
  metaPlacas: number;
  realizado: number;
  editavel: boolean;
}

interface RespostaMetas {
  competencia: string;
  linhas: LinhaMeta[];
}

export default function PainelMetas() {
  const [comp] = useState(competenciaAtual());
  const { pode } = useAuth();
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['metas', comp],
    queryFn: () => api.get<RespostaMetas>(`/metas?competencia=${comp}`),
  });

  const salvar = useMutation({
    mutationFn: (v: { escopo: string; escopoId: string | null; metaPlacas: number }) =>
      api.put('/metas', { ...v, competencia: comp }),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['metas', comp] }),
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-3">
        <Esqueleto className="h-24" />
        <Esqueleto className="h-24" />
      </div>
    );
  }

  if (!data || data.linhas.length === 0) {
    return (
      <Card className="p-2">
        <EstadoVazio
          icone={<Target size={24} />}
          titulo="Nenhuma meta definida"
          descricao={
            pode('admin', 'diretor')
              ? 'Defina a meta global do mês para começar a distribuir entre as regionais.'
              : 'A diretoria ainda não publicou as metas desta competência.'
          }
        />
      </Card>
    );
  }

  return (
    <div className="flex flex-col gap-3">
      <p className="px-1 text-[13px] capitalize text-[var(--muted)]">{competencia(comp)}</p>

      {data.linhas.map((linha) => (
        <CartaoMeta
          key={`${linha.escopo}:${linha.escopoId ?? 'global'}`}
          linha={linha}
          salvando={salvar.isPending}
          aoSalvar={(metaPlacas) =>
            salvar.mutate({ escopo: linha.escopo, escopoId: linha.escopoId, metaPlacas })
          }
        />
      ))}
    </div>
  );
}

function CartaoMeta({
  linha,
  aoSalvar,
  salvando,
}: {
  linha: LinhaMeta;
  aoSalvar: (meta: number) => void;
  salvando: boolean;
}) {
  const [valor, setValor] = useState(String(linha.metaPlacas));
  const alterado = Number(valor) !== linha.metaPlacas && valor !== '';
  const pct = linha.metaPlacas > 0 ? (linha.realizado / linha.metaPlacas) * 100 : 0;

  return (
    <Card className="p-5">
      <div className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="text-xs text-[var(--faint)]">
            {linha.escopo === 'global' ? 'Associação' : linha.escopo}
          </p>
          <h3 className="truncate text-sm font-medium text-[var(--fg)]">{linha.nome}</h3>
        </div>
        {!linha.editavel && (
          <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-[var(--faint)]">
            <Lock size={12} /> somente leitura
          </span>
        )}
      </div>

      <div className="mb-4 flex items-baseline gap-2">
        <span className="num text-2xl font-semibold text-[var(--fg)]">
          {numero(linha.realizado)}
        </span>
        <span className="text-sm text-[var(--muted)]">de {numero(linha.metaPlacas)} placas</span>
        <span
          className="ml-auto num text-sm font-medium"
          style={{ color: pct >= 100 ? 'var(--success)' : 'var(--brand)' }}
        >
          {percentual(pct)}
        </span>
      </div>

      <div className="mb-4 h-1.5 overflow-hidden rounded-[var(--r-full)] bg-[var(--gray-200)]">
        <div
          className="h-full rounded-[var(--r-full)]"
          style={{
            width: `${Math.min(pct, 100)}%`,
            background: pct >= 100 ? 'var(--success)' : 'var(--brand)',
          }}
        />
      </div>

      {linha.editavel && (
        <div className="flex items-end gap-3">
          <Campo
            rotulo="Meta de placas"
            type="number"
            inputMode="numeric"
            min={0}
            value={valor}
            onChange={(e) => setValor(e.target.value)}
          />
          <Botao
            variante="primario"
            tamanho="lg"
            disabled={!alterado}
            carregando={salvando}
            icone={<Check size={16} />}
            onClick={() => aoSalvar(Number(valor))}
          >
            Salvar
          </Botao>
        </div>
      )}
    </Card>
  );
}
